import { useCustom } from "@refinedev/core";
import { AlertTriangle, ClipboardList } from "lucide-react";
import { useMemo } from "react";
import { Link, useSearchParams } from "react-router";

import { ApiError, query } from "@/lib/api";
import { errorMessage, stamp } from "@/lib/format";
import { pageData } from "@/lib/loaded";
import {
  PLATFORMS,
  TASK_STATUS_ALL,
  TASK_STATUS_OPEN,
  shelfName,
  statusTone,
} from "@/lib/podsort";
import type { PodsortTask, PodsortTasks } from "@/lib/providers";
import { useSessionGuard } from "@/lib/useSessionGuard";

/** Сколько строк добавляет одно нажатие «показать ещё». Ровно страница API. */
const PAGE = 100;

const TONE: Record<ReturnType<typeof statusTone>, string> = {
  wait: "border-accent/50 text-accent-text",
  work: "border-fg/40 text-fg",
  done: "border-ok/50 text-ok",
  off: "border-border text-fg-muted",
};

/**
 * Подсорт → Задачи: что склад уже поставил в работу (задача владельца
 * 17.09.26 — «вывод таблицы задач из таблицы FBS»).
 *
 * Строки — таблица соседа `fbs.fbs_podsort_task`, одна строка = одна штука.
 * Ставят их кнопкой «Создать» на «Расчёте», берёт в корзину сборщик в FBS,
 * отменяют там же. Экран ТОЛЬКО ЧИТАЕТ: своей копии задач у склада нет, и
 * вторая копия разошлась бы с первой на первом же подборе.
 *
 * Отбор — площадкой, городом и статусом, и живёт он в АДРЕСЕ: «Расчёт» после
 * «Создать» ведёт сюда суженным той же площадкой и тем же городом. Числа на
 * переключателях считает сервер «как если переключить».
 */
export function PodsortTasksPage() {
  const [params, setParams] = useSearchParams();
  const platform = params.get("platform") ?? "";
  const city = params.get("city") ?? "";
  const status = params.get("status") ?? "";
  const limitParam = params.get("n") ?? "";

  /** Правит ОДИН ключ адреса, не трогая остальные. */
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== "n") next.delete("n");
    setParams(next, { replace: true });
  };

  const url = useMemo(
    () =>
      `/api/podsort/tasks${query({
        platform: platform || undefined,
        city_id: city || undefined,
        status: status || undefined,
        limit: limitParam || undefined,
      })}`,
    [platform, city, status, limitParam],
  );

  const listQuery = useCustom<PodsortTasks>({ url, method: "get" });
  useSessionGuard(listQuery.query.error);
  const list = pageData(listQuery.result.data, "rows");
  const busy = listQuery.query.isFetching;
  const error = listQuery.query.error;

  const total = list?.statuses.reduce((n, s) => n + s.count, 0) ?? 0;
  const open =
    list?.statuses
      .filter((s) => s.code === "new" || s.code === "in_progress")
      .reduce((n, s) => n + s.count, 0) ?? 0;

  return (
    <div
      className="flex h-full min-h-0 flex-col gap-3 p-4"
      data-component="PodsortTasksPage"
      data-annot="подсорт задачи"
    >
      <div>
        <h1 className="text-[15px] font-semibold">Подсорт · Задачи</h1>
        <p className="mt-0.5 text-[12.5px] text-fg-muted">
          Что поставлено в работу: одна строка — одна штука. Берут задачи
          сборщики в FBS, в разделе «Подсорт»; новые сверху.{" "}
          <Link
            to={`/podsort${query({
              platform: platform === "ozon" ? "ozon" : undefined,
              city: city || undefined,
            })}`}
            className="text-fg underline underline-offset-2"
          >
            К расчёту
          </Link>
        </p>
      </div>

      {error ? (
        <div
          role="alert"
          className="flex flex-col items-center gap-3 rounded-[var(--radius-card)] border border-err/60 bg-err-bg p-6 text-center"
        >
          <AlertTriangle size={20} className="text-err" />
          <p className="text-[13.5px]">
            {error instanceof ApiError && error.message === "fbs_db_disabled"
              ? "Базы задач FBS в этой полосе нет: смотреть задачи негде"
              : error instanceof ApiError && error.status === 503
                ? "База задач FBS или CRM не отвечает — список не показан, а не пуст"
                : errorMessage(error)}
          </p>
          <button
            type="button"
            onClick={() => listQuery.query.refetch()}
            className="rounded-[var(--radius-ui)] border border-border px-2.5 py-1 text-[12.5px] hover:bg-bg-hover"
          >
            Повторить
          </button>
        </div>
      ) : !list ? (
        <p className="text-[13px] text-fg-muted">Читаю задачи…</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2" data-annot="отбор">
            <div
              data-annot="площадка"
              className="inline-flex flex-wrap rounded-[var(--radius-ui)] border border-border p-0.5"
            >
              <Chip active={!platform} onClick={() => set("platform", "")}>
                все площадки
              </Chip>
              {PLATFORMS.map((p) => (
                <Chip
                  key={p.value}
                  active={platform === p.value}
                  onClick={() => set("platform", p.value)}
                >
                  {p.label}{" "}
                  <span className="tnum opacity-60">
                    {list.platforms.find((one) => one.platform === p.value)?.count ?? 0}
                  </span>
                </Chip>
              ))}
            </div>

            {list.cities.length > 0 ? (
              <div
                data-annot="город"
                className="inline-flex flex-wrap rounded-[var(--radius-ui)] border border-border p-0.5"
              >
                <Chip active={!city} onClick={() => set("city", "")}>
                  все города
                </Chip>
                {list.cities.map((one) => (
                  <Chip
                    key={one.city_id}
                    active={city === String(one.city_id)}
                    onClick={() => set("city", String(one.city_id))}
                  >
                    {one.name} <span className="tnum opacity-60">{one.count}</span>
                  </Chip>
                ))}
              </div>
            ) : null}

            <div
              data-annot="статус"
              className="inline-flex flex-wrap rounded-[var(--radius-ui)] border border-border p-0.5"
            >
              <Chip
                active={!status || status === TASK_STATUS_ALL}
                onClick={() => set("status", "")}
              >
                все <span className="tnum opacity-60">{total}</span>
              </Chip>
              {/* «Открытые» — не статус, а вопрос смены: что ещё не в
                  корзине. */}
              <Chip
                active={status === TASK_STATUS_OPEN}
                onClick={() => set("status", TASK_STATUS_OPEN)}
              >
                открытые <span className="tnum opacity-60">{open}</span>
              </Chip>
              {list.statuses.map((one) => (
                <Chip
                  key={one.code}
                  active={status === one.code}
                  onClick={() => set("status", one.code)}
                >
                  {one.name} <span className="tnum opacity-60">{one.count}</span>
                </Chip>
              ))}
            </div>
          </div>

          {list.rows.length === 0 ? (
            <div className="rounded-[var(--radius-card)] border border-border bg-bg-subtle p-6 text-center text-[13px] text-fg-muted">
              <ClipboardList size={20} className="mx-auto mb-2" />
              Под этот отбор задач подсорта нет.
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto rounded-[var(--radius-card)] border border-border bg-bg-subtle">
              <table data-annot="таблица" className="w-full border-collapse text-[13px]">
                <caption className="sr-only">Задачи подсорта</caption>
                <thead className="hidden md:table-header-group">
                  <tr>
                    {[
                      "Задача",
                      "Товар",
                      "Площадка · город",
                      "Статус",
                      "Поставил",
                      "Подобрал",
                      "Откуда → куда",
                    ].map((title) => (
                      <th
                        key={title}
                        scope="col"
                        className="sticky top-0 z-10 whitespace-nowrap border-b border-border bg-bg-elevated px-2.5 py-2 text-left text-[11px] font-medium uppercase tracking-[0.04em] text-fg"
                      >
                        {title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="block md:table-row-group">
                  {list.rows.map((row) => (
                    <TaskRow key={row.task_id} row={row} />
                  ))}
                </tbody>
              </table>
              {list.shown < list.total ? (
                <div className="border-t border-border p-3 text-center">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => set("n", String(list.shown + PAGE))}
                    className="h-8 rounded-[var(--radius-ui)] border border-border px-3 text-[13px] hover:bg-bg-hover disabled:opacity-60"
                  >
                    {busy ? "Гружу…" : `Показать ещё ${Math.min(PAGE, list.total - list.shown)}`}
                  </button>
                  <p className="mt-1.5 text-[11.5px] text-fg-muted">
                    Показано {list.shown} из {list.total}
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Строка задачи: штука, её товар, чья работа и где вещь сейчас. */
function TaskRow({ row }: { row: PodsortTask }) {
  const tone = TONE[statusTone(row.status.code)];
  return (
    <tr
      data-annot={`задача ${row.task_id}`}
      className="mb-2 block rounded-[var(--radius-ui)] border border-border bg-bg p-2 md:mb-0 md:table-row md:rounded-none md:border-0 md:border-b md:border-border md:bg-transparent md:p-0 md:hover:bg-bg-hover"
    >
      <Cell label="Задача">
        <span className="flex flex-col">
          <span className="tnum font-medium">{row.task_id}</span>
          <span className="tnum whitespace-nowrap text-[11px] text-fg-muted">
            {row.created_at ? stamp(row.created_at) : "—"}
          </span>
        </span>
      </Cell>
      <Cell label="Товар">
        <span className="flex items-center gap-2">
          <span className="flex h-[44px] w-[33px] flex-none items-center justify-center overflow-hidden rounded-[var(--radius-ui)] border border-border bg-bg-subtle text-[10px] text-fg-muted">
            {row.card?.image_url ? (
              <img
                src={row.card.image_url}
                alt={row.card.name}
                referrerPolicy="no-referrer"
                loading="lazy"
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="tnum">{row.card?.model_id || "—"}</span>
            )}
          </span>
          <span className="flex min-w-0 flex-col">
            {row.card ? (
              <Link
                to={`/barcode/${row.card.barcode}`}
                className="tnum truncate font-medium hover:text-accent-text"
                title={row.card.name}
              >
                {row.card.barcode}
              </Link>
            ) : (
              <span className="tnum">карточка {row.card_id}</span>
            )}
            <span className="truncate text-[11px] text-fg-muted">{row.card?.size ?? ""}</span>
          </span>
        </span>
      </Cell>
      <Cell label="Площадка · город">
        <span className="whitespace-nowrap">
          {row.platform ? shelfName(row.platform === "ozon" ? "ozon" : "wb") : "—"}
          <span className="text-fg-muted"> · {row.city.name}</span>
        </span>
      </Cell>
      <Cell label="Статус">
        <span className="flex flex-col items-start gap-0.5">
          <span
            className={
              "whitespace-nowrap rounded-[var(--radius-ui)] border px-1.5 py-px text-[12px] " + tone
            }
          >
            {row.status.name}
          </span>
          <span className="tnum whitespace-nowrap text-[11px] text-fg-muted">
            {row.done_at
              ? stamp(row.done_at)
              : row.cancelled_at
                ? stamp(row.cancelled_at)
                : row.claimed_at
                  ? stamp(row.claimed_at)
                  : ""}
          </span>
        </span>
      </Cell>
      {/* КТО ПОСТАВИЛ — подпись склада; у задач, поставленных не кнопкой
          «Создать» (генератор FBS), её нет, и экран говорит это словами, а не
          пустой клеткой. */}
      <Cell label="Поставил">
        <span
          className={row.author ? "" : "text-fg-muted"}
          title={row.author ? undefined : "Поставлена не кнопкой «Создать» — например, генератором FBS"}
        >
          {row.author ? row.author.name || `#${row.author.user_id}` : "не через wms"}
        </span>
      </Cell>
      <Cell label="Подобрал">
        <span className="flex flex-col">
          <span className={row.user ? "" : "text-fg-muted"}>
            {row.user ? row.user.name || `#${row.user.user_id}` : "—"}
          </span>
          {row.serial ? (
            <Link
              to={`/serial/${row.serial}`}
              className="tnum text-[11px] text-fg-muted hover:text-accent-text"
            >
              серия {row.serial}
            </Link>
          ) : null}
        </span>
      </Cell>
      <Cell label="Откуда → куда">
        {row.from_place || row.basket ? (
          <span className="flex flex-wrap items-center gap-x-1 text-[12.5px]">
            {row.from_place ? (
              <Link to={`/place/${row.from_place.place_id}`} className="hover:text-accent-text">
                {row.from_place.name || `Место ${row.from_place.place_id}`}
              </Link>
            ) : (
              "—"
            )}
            <span className="text-fg-muted">→</span>
            {row.basket ? (
              <Link to={`/place/${row.basket.place_id}`} className="hover:text-accent-text">
                {row.basket.name || `Место ${row.basket.place_id}`}
              </Link>
            ) : (
              "—"
            )}
          </span>
        ) : (
          <span className="text-fg-muted">ещё в коробе</span>
        )}
      </Cell>
    </tr>
  );
}

/** Кнопка-чип отбора. Нажатая помечена `aria-pressed`, а не только цветом. */
function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={
        "h-7 rounded-[calc(var(--radius-ui)-2px)] px-2.5 text-[12.5px] " +
        (active ? "bg-accent-dim font-medium text-accent-text" : "text-fg-muted hover:text-fg")
      }
    >
      {children}
    </button>
  );
}

/** Ячейка, которая на узком экране становится строкой «подпись — значение». */
function Cell({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <td
      data-label={label}
      data-annot={label}
      className={
        "flex items-center justify-between gap-3 border-b border-border/60 py-1 last:border-0 " +
        "before:text-[11px] before:uppercase before:tracking-[0.04em] before:text-fg-muted before:content-[attr(data-label)] " +
        "md:table-cell md:border-0 md:px-2.5 md:py-1.5 md:align-middle md:before:content-none"
      }
    >
      {children}
    </td>
  );
}
